import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useUnsavedChangesWarning } from '@brands/hooks/useUnsavedChangesWarning';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';
import { COLLECTOR_CASE_CREATION_BASE_PATH } from '@brands/Utils/reviewProfileBasePath';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { E164Number } from 'libphonenumber-js';
import { isEqual } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IoAdd,
  IoChatbubbleEllipsesOutline,
  IoChevronBack,
  IoChevronDown,
  IoDocumentTextOutline,
  IoEllipsisVertical,
} from 'react-icons/io5';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import HttpsOutlinedIcon from '@mui/icons-material/HttpsOutlined';
import Container from '../../../../Components/Container/Container';
import { Loading } from '../../../../Components/LoadingSpinner/Loading';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import MultiSelectInputMUI from '@brands/Components/Inputs/MuiMultiSelectInput/MultiSelectInputMUI';
import PhoneInput from '@brands/Components/Inputs/MuiTelInput/MuiTelInput';
import {
  isCollectorDependentActive,
  isCollectorDependentDeleted,
} from '../../../../services/identity/collectorDependents';
import { getCollectorUserById } from '../../../../services/identity/getCollectorUserById';
import { getUserAvatar } from '../../../../services/identity/getUserAvatar';
import { isCollectorManagedUserId } from '../../../../services/identity/listCollectorUsers';
import { resolveLanguageSelection } from '../../../../services/identity/normalizeCollectorUserResponse';
import { listAllClientTags } from '../../../../services/identity/listAllClientTags';
import { listAllLanguages } from '../../../../services/identity/listAllLanguages';
import { UserRoleName } from '../../../../services/identity/types/UserProfile';
import {
  type CollectorUserEditFormState,
  mapCollectorUserEditFormToApiPayload,
  type UpdateCollectorUserPayload,
  validateUpdateCollectorUserPayload,
} from '../../../../services/identity/collectorUserEditPayload';
import { updateCollectorUser } from '../../../../services/identity/updateCollectorUser';
import { getClientDivisionOptions } from '../../../../services/identity/clientDivisionOptions';
import { scopeDivisionOptionsToActor } from '../../../../services/identity/clientDivisionActorScope';
import { genderIdentity, genderOptions, pronounsOptions } from '../../../../Utils/selectButtonOptions';
import { stateOptions } from '../../../../Utils/selectOptions';
import { displayCollectorUserPatchError, displayErrorDetails } from '../../../../Utils/displayError';
import AccountSettingsTab from './CollectorAccountSettingsTab';
import CollectorAddDependentModal from './CollectorAddDependentModal';
import InsuranceTab from './CollectorInsuranceTab';
import MedicalHistoryTab from './CollectorMedicalHistoryTab';
import PharmacyTab from './CollectorPharmacyTab';
import ProfileCasesSection from '@brands/Dashboard/MyProfile/Components/ProfileCasesSection';
import { config } from '@brands/config/config';
import addNewUserStyles from '../AddNewUser/addNewUser.module.scss';
import styles from './collectorEditUser.module.scss';
import { dateAutoSlashes } from '../../../../Utils/checkAndSetDate';
import { ClientTag, Language, UserProfile } from '../../../../services/identity/types/UserProfile';
import AddEmailModal from '@brands/Dashboard/Dashboard/SuperAdmin/Components/AddEmailModal/AddEmailModal';
import { reinviteUserById } from '@brands/services/identity/reinviteUserById';
import ResetPasswordModal from '@brands/Dashboard/MyProfile/Modal/ResetPasswordModal';
import ChangeUserStatusModal from '@brands/Dashboard/MyProfile/Modal/ChangeUserStatusModal';
import UnsavedChangesModal from '@brands/Dashboard/Dashboard/Components/UnsavedChangesModal/UnsavedChangesModal';
import { deleteCollectorUserById } from '@brands/services/identity/deleteCollectorUserById';
import { reactivateCollectorUserById } from '@brands/services/identity/reactivateCollectorUserById';
import { deleteCollectorDependent } from '@brands/services/identity/deleteCollectorDependent';
import { updateVerifiedEmail } from '@brands/services/identity/updateUserEmail';
import { isValidEmail } from '../../../../Utils/validation';
import { resolveGuardianDisplayName, resolveProfileDisplayName } from '../../../../Utils/guardianDisplay';

const profileTabs = ['Profile', 'Medical history', 'Insurance', 'Pharmacy', 'Cases', 'Account settings'] as const;
type ProfileTab = (typeof profileTabs)[number];
const dependentProfileTabs: ProfileTab[] = ['Profile', 'Medical history', 'Insurance', 'Pharmacy', 'Cases'];
const relationshipOptions = [
  { value: 'Parent', label: 'Parent' },
  { value: 'Stepparent', label: 'Stepparent' },
  { value: 'Sibling', label: 'Sibling' },
  { value: 'Aunt/Uncle', label: 'Aunt/Uncle' },
  { value: 'Grandparent', label: 'Grandparent' },
  { value: 'Appointed Guardian', label: 'Appointed Guardian' },
  { value: 'Other', label: 'Other' },
];

const TAB_QUERY_KEY = 'tab';

const tabToSlug: Record<ProfileTab, string> = {
  Profile: 'profile',
  'Medical history': 'medical-history',
  Insurance: 'insurance',
  Pharmacy: 'pharmacy',
  Cases: 'cases',
  'Account settings': 'account-settings',
};

const slugToTab = Object.fromEntries(profileTabs.map((tab) => [tabToSlug[tab], tab])) as Record<string, ProfileTab>;

const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

/** Subtitle under each client tag (product-level services). */
const CLIENT_TAG_SERVICES_SUMMARY = 'Live consultation, Phone consultation, Photo review, Second Opinion';

function primaryClientTagIdsFromUserClients(clients: ClientTag[] | undefined): number[] {
  const ids = (clients ?? []).map((c) => Number(c.id)).filter((id) => Number.isInteger(id) && id > 0);
  return ids.length ? [ids[0]] : [];
}

function profileHasUploadedPhoto(profile: Pick<UserProfile, 'picture_media_id'> | null | undefined): boolean {
  if (!profile?.picture_media_id) return false;
  const id = String(profile.picture_media_id).trim().toLowerCase();
  return id !== '' && id !== '0' && id !== 'null' && id !== 'undefined';
}

function resolveCollectorAssignableClientTags(
  apiClientTags: ClientTag[] | undefined,
  authClientTags: ClientTag[] | undefined,
): ClientTag[] {
  const validApiClientTags = (apiClientTags || []).filter(
    (tag) => Number.isInteger(Number(tag.id)) && Number(tag.id) > 0,
  );

  if (Array.isArray(authClientTags)) {
    const apiById = new Map(validApiClientTags.map((tag) => [Number(tag.id), tag]));
    const seenIds = new Set<number>();

    return authClientTags.reduce<ClientTag[]>((acc, authTag) => {
      const id = Number(authTag?.id);
      const apiTag = apiById.get(id);
      if (!Number.isInteger(id) || id <= 0 || seenIds.has(id) || !apiTag) return acc;
      seenIds.add(id);
      acc.push(apiTag);
      return acc;
    }, []);
  }

  return validApiClientTags.filter((tag) => Boolean(tag.code));
}

function resolveEditableClientTags(
  userClientTags: ClientTag[] | undefined,
  collectorClientTags: ClientTag[],
): ClientTag[] {
  const currentClientTagId = primaryClientTagIdsFromUserClients(userClientTags)[0];
  if (!currentClientTagId) return collectorClientTags;

  const collectorTagById = new Map(collectorClientTags.map((tag) => [Number(tag.id), tag]));
  const matchingCollectorTag = collectorTagById.get(currentClientTagId);
  if (matchingCollectorTag) return collectorClientTags;

  const currentClientTag = userClientTags?.find((tag) => Number(tag.id) === currentClientTagId);
  return currentClientTag ? [currentClientTag, ...collectorClientTags] : collectorClientTags;
}

/** API returns YYYY-MM-DD; create flow uses MM/DD/YYYY in the field. */
function dobFromApi(iso: string): string {
  if (!iso) return '';
  const m = moment(iso, ['YYYY-MM-DD', moment.ISO_8601], true);
  return m.isValid() ? m.format('MM/DD/YYYY') : iso;
}

function dobToApi(display: string): string {
  if (!display.trim()) return '';
  const m = moment(display.trim(), 'MM/DD/YYYY', true);
  if (m.isValid()) return m.format('YYYY-MM-DD');
  const m2 = moment(display.trim(), 'YYYY-MM-DD', true);
  return m2.isValid() ? display.trim() : display.trim();
}

function collectorUserProfileToEditFormState(
  userData: UserProfile,
  languagesCatalog: Language[],
): CollectorUserEditFormState {
  return {
    firstName: userData.basic_info?.first_name || '',
    middleName: userData.basic_info?.middle_name || '',
    lastName: userData.basic_info?.last_name || '',
    preferredName: userData.basic_info?.preferred_name || '',
    dateOfBirth: dobFromApi(userData.basic_info?.date_of_birth || ''),
    gender: userData.basic_info?.gender || '',
    genderIdentity: userData.basic_info?.gender_identity || '',
    pronouns: userData.basic_info?.pronouns || '',
    selectedLanguages: resolveLanguageSelection(userData.basic_info?.languages ?? userData.languages, languagesCatalog),
    address1: userData.address?.address1 || '',
    address2: userData.address?.address2 || '',
    city: userData.address?.city || '',
    state: userData.address?.state || '',
    zipCode: userData.address?.zip_code || '',
    phoneNumber: userData.phone_number || '',
    selectedClientTagIds: primaryClientTagIdsFromUserClients(userData.clients),
    selectedClientDivisionId: userData.divisions?.[0]?.id ?? null,
    email: userData.email || '',
    externalId: userData.external_id || '',
  };
}

const EditUserPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { userInfo: authUser } = useAppSelector(selectAuth);
  const navigate = useRoleNavigate();
  const navigateRaw = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState<UserProfile | null>(null);

  const activeTab = useMemo((): ProfileTab => {
    const slug = searchParams.get(TAB_QUERY_KEY)?.toLowerCase() ?? '';
    return slugToTab[slug] ?? 'Profile';
  }, [searchParams]);

  const [isInvitedUser, setIsInvitedUser] = useState(false);
  const [resetPasswordConfirmation, setResetPasswordConfirmation] = useState<{ isOpen: boolean; userState: string }>({
    isOpen: false,
    userState: '',
  });
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const disableButton = (): void => {
    setResetEmailSent(true);
  };

  const setActiveTab = useCallback(
    (tab: ProfileTab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(TAB_QUERY_KEY, tabToSlug[tab]);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Form state
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [gender, setGender] = useState('');
  const [genderIdentityValue, setGenderIdentityValue] = useState('');
  const [pronouns, setPronouns] = useState('');
  const [selectedLanguages, setSelectedLanguages] = useState<Language[]>([]);
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [state, setStateValue] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [guardianRelationship, setGuardianRelationship] = useState('');
  const [guardianAddress, setGuardianAddress] = useState<UserProfile['address'] | null>(null);
  const [fetchedGuardianName, setFetchedGuardianName] = useState<string | null>(null);
  const [selectedClientTagIds, setSelectedClientTagIds] = useState<number[]>([]);
  const [selectedClientDivisionId, setSelectedClientDivisionId] = useState<number | null>(null);
  const [externalId, setExternalId] = useState('');

  // Reference data
  const [allLanguages, setAllLanguages] = useState<Language[]>([]);
  const [apiCollectorClientTags, setApiCollectorClientTags] = useState<ClientTag[]>([]);
  const [dependentModalOpen, setDependentModalOpen] = useState(false);
  /** `0` = add new dependent in modal; otherwise dependent user id to edit */
  const [dependentModalTargetId, setDependentModalTargetId] = useState(0);
  const [deactivatedSectionOpen, setDeactivatedSectionOpen] = useState(false);
  const [userHeaderMenuAnchor, setUserHeaderMenuAnchor] = useState<null | HTMLElement>(null);
  // Opened by `inviteUser` when the backend rejects a re-invite with
  // "User has no email" — happens after Quick Add patient creation
  // (DO-4070). Mirrors the Admin pattern in
  // `ModernReviewProfileHeader.tsx`: prompt the collector to add an
  // email, save it through `updateUserEmail`, then re-run the invite.
  const [displayEmailModal, setDisplayEmailModal] = useState(false);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [deactivateUserModal, setDeactivateUserModal] = useState('');
  const [activateUserModal, setActivateUserModal] = useState('');
  const [deleteDependentModal, setDeleteDependentModal] = useState('');
  const [headerAvatarLoadFailed, setHeaderAvatarLoadFailed] = useState(false);
  const [dependentAvatarFailedIds, setDependentAvatarFailedIds] = useState<Record<number, boolean>>({});

  /** Last saved / loaded profile as PATCH body; used to disable Save until the form changes. */
  const savedProfilePayloadRef = useRef<UpdateCollectorUserPayload | null>(null);

  /** Shared with whichever tab is mounted so an unsaved-changes save action can submit its form. */
  const formRef = useRef<HTMLFormElement>(null);
  const [medicalHistoryDirty, setMedicalHistoryDirty] = useState(false);
  const [insuranceDirty, setInsuranceDirty] = useState(false);

  const collectorClientTags = useMemo(
    () => resolveCollectorAssignableClientTags(apiCollectorClientTags, authUser?.clients),
    [apiCollectorClientTags, authUser?.clients],
  );

  const allClientTags = useMemo(
    () => resolveEditableClientTags(user?.clients, collectorClientTags),
    [user?.clients, collectorClientTags],
  );

  const clientTagOptions = useMemo(
    () => allClientTags.map((tag) => ({ id: String(tag.id), value: String(tag.id), label: tag.name || 'Client tag' })),
    [allClientTags],
  );

  const divisionOptions = useMemo(
    () =>
      scopeDivisionOptionsToActor(
        getClientDivisionOptions(allClientTags, selectedClientTagIds).map((option) => ({
          id: option.id,
          value: String(option.value),
          label: option.label,
        })),
        authUser,
        selectedClientDivisionId != null ? [selectedClientDivisionId] : [],
      ),
    [allClientTags, selectedClientTagIds, authUser, selectedClientDivisionId],
  );
  // Division is only required when the actor is themselves confined to a
  // division (not merely whole-tag) -- an unconfined CA/Collector can leave
  // a patient's division unset. A confined actor's own division(s) are the
  // only ones `divisionOptions` ever contains (see `scopeDivisionOptionsToActor`).
  // Also requires the field to actually be rendered (`divisionOptions.length
  // > 0`): a confined actor can still land on a tag with no divisions of
  // its own, and without this the requirement would survive with no field
  // left to satisfy it, permanently blocking save.
  const divisionRequired =
    Array.isArray(authUser?.divisions) && authUser.divisions.length > 0 && divisionOptions.length > 0;

  // A confined actor with exactly one division has no real choice to make --
  // pre-check it so they don't have to click the only option, while leaving
  // it interactive: they can still uncheck it, which blocks save (required)
  // until it's re-checked. Never overrides a division the patient already has.
  const autoSelectedDivisionRef = useRef<number | null>(null);
  useEffect(() => {
    if (divisionRequired && divisionOptions.length === 1) {
      const onlyValue = Number(divisionOptions[0].value);
      if (autoSelectedDivisionRef.current !== onlyValue && selectedClientDivisionId == null) {
        autoSelectedDivisionRef.current = onlyValue;
        setSelectedClientDivisionId(onlyValue);
      }
    } else {
      autoSelectedDivisionRef.current = null;
    }
  }, [divisionOptions, selectedClientDivisionId, divisionRequired]);

  useEffect(() => {
    setDeactivatedSectionOpen(false);
  }, [userId]);

  useEffect(() => {
    setHeaderAvatarLoadFailed(false);
    setDependentAvatarFailedIds({});
  }, [userId]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const allowed = await isCollectorManagedUserId(Number(userId));
        if (!allowed) {
          toast.error('This user is not in your list. Open them from Users.', { className: 'fs-unmask' });
          navigateRaw(-1);
          return;
        }

        const [userData, languages, clientTags] = await Promise.all([
          getCollectorUserById(Number(userId)),
          listAllLanguages(),
          listAllClientTags(UserRoleName.Collector),
        ]);
        const formState = collectorUserProfileToEditFormState(userData, languages);

        // Fold the single-division auto-select into the initial load itself,
        // computed straight off this fetch's own data rather than the
        // `allClientTags`/`divisionOptions` memos below (their state isn't
        // set yet at this point). Doing it here -- before the dirty
        // snapshot is taken -- keeps a freshly-opened patient with no
        // division from reading as dirty purely from loading the page; the
        // effect further down only needs to handle it changing afterward
        // (e.g. the actor switching client tags).
        let initialDivisionId = formState.selectedClientDivisionId ?? null;
        if (initialDivisionId == null && Array.isArray(authUser?.divisions) && authUser.divisions.length > 0) {
          const collectorClientTagsForLoad = resolveCollectorAssignableClientTags(clientTags.nodes, authUser?.clients);
          const allClientTagsForLoad = resolveEditableClientTags(userData.clients, collectorClientTagsForLoad);
          const divisionOptionsForLoad = scopeDivisionOptionsToActor(
            getClientDivisionOptions(allClientTagsForLoad, formState.selectedClientTagIds).map((option) => ({
              id: option.id,
              value: String(option.value),
              label: option.label,
            })),
            authUser,
          );
          if (divisionOptionsForLoad.length === 1) {
            initialDivisionId = Number(divisionOptionsForLoad[0].value);
            autoSelectedDivisionRef.current = initialDivisionId;
          }
        }

        setUser(userData);
        setFirstName(formState.firstName);
        setMiddleName(formState.middleName);
        setLastName(formState.lastName);
        setPreferredName(formState.preferredName);
        setDateOfBirth(formState.dateOfBirth);
        setEmail(userData.email || '');
        setPhoneNumber(formState.phoneNumber);
        setGender(formState.gender);
        setGenderIdentityValue(formState.genderIdentity);
        setPronouns(formState.pronouns);
        setSelectedLanguages(formState.selectedLanguages);
        setAddress1(formState.address1);
        setAddress2(formState.address2);
        setCity(formState.city);
        setStateValue(formState.state);
        setZipCode(formState.zipCode);
        setGuardianRelationship(userData.guardian_relationship || '');
        setGuardianAddress(null);
        setSelectedClientTagIds(formState.selectedClientTagIds);
        setSelectedClientDivisionId(initialDivisionId);
        setExternalId(formState.externalId ?? '');

        savedProfilePayloadRef.current = mapCollectorUserEditFormToApiPayload(
          {
            ...formState,
            dateOfBirth: dobToApi(formState.dateOfBirth),
            selectedClientTagIds: formState.selectedClientTagIds,
            selectedClientDivisionId: initialDivisionId,
            guardianRelationship: userData.guardian_relationship || '',
            email: userData.email || '',
            externalId: userData.external_id || '',
          },
          { isDependent: userData.role?.name === UserRoleName.Dependent },
        );

        setAllLanguages(languages);
        setApiCollectorClientTags(clientTags.nodes);
      } catch (error) {
        displayErrorDetails(error);
      } finally {
        setLoading(false);
      }
    };
    if (userId) fetchData();
  }, [navigateRaw, userId]);

  useEffect(() => {
    let cancelled = false;
    const fetchGuardianAddress = async (): Promise<void> => {
      if (user?.role?.name !== UserRoleName.Dependent || !user?.guardian_id) {
        if (!cancelled) setGuardianAddress(null);
        if (!cancelled) setFetchedGuardianName(null);
        return;
      }
      const nestedGuardianName = resolveGuardianDisplayName(user);
      if (!cancelled) setFetchedGuardianName(nestedGuardianName);
      if (user.guardian?.address) {
        if (!cancelled) setGuardianAddress(user.guardian.address);
        return;
      }
      try {
        const guardian = await getCollectorUserById(Number(user.guardian_id));
        if (!cancelled) {
          setGuardianAddress(guardian.address ?? null);
          setFetchedGuardianName(resolveProfileDisplayName(guardian) || nestedGuardianName);
        }
      } catch {
        if (!cancelled) {
          setGuardianAddress(null);
          setFetchedGuardianName(nestedGuardianName);
        }
      }
    };
    void fetchGuardianAddress();
    return () => {
      cancelled = true;
    };
  }, [user?.role?.name, user?.guardian_id, user?.guardian]);

  const allDependents = useMemo(() => user?.dependents ?? [], [user?.dependents]);

  const activeDependents = useMemo(
    () => allDependents.filter((dep) => isCollectorDependentActive(dep)),
    [allDependents],
  );

  const deletedDependents = useMemo(
    () => allDependents.filter((dep) => isCollectorDependentDeleted(dep)),
    [allDependents],
  );

  const isPatientUser = user?.role?.name === UserRoleName.Patient;
  const isDependentUser = user?.role?.name === UserRoleName.Dependent;
  const isAdminRole = useMemo(
    () =>
      authUser.role.name === UserRoleName.Admin ||
      authUser.role.name === UserRoleName.SuperAdmin ||
      authUser.role.name === UserRoleName.OrganizationAdmin ||
      authUser.role.name === UserRoleName.Collector ||
      authUser.role.name === UserRoleName.CareAdvisor,
    [authUser.role.name],
  );

  const visibleProfileTabs = useMemo(() => {
    const base = isDependentUser ? dependentProfileTabs : profileTabs;
    return config.features.multiStage ? base : base.filter((tab) => tab !== 'Cases');
  }, [isDependentUser]);
  const isSameAddressAsGuardian =
    address1 === (guardianAddress?.address1 ?? '') &&
    address2 === (guardianAddress?.address2 ?? '') &&
    city === (guardianAddress?.city ?? '') &&
    state === (guardianAddress?.state ?? '') &&
    zipCode === (guardianAddress?.zip_code ?? '');

  useEffect(() => {
    if (isDependentUser && activeTab === 'Account settings') {
      setActiveTab('Profile');
    } else if (activeTab === 'Cases' && !config.features.multiStage) {
      setActiveTab('Profile');
    }
  }, [isDependentUser, activeTab, setActiveTab]);

  const headerDisplayName = useMemo(() => {
    const fromParts = [firstName, lastName].filter(Boolean).join(' ').trim();
    if (fromParts) return fromParts;
    return user?.full_name?.trim() || '—';
  }, [firstName, lastName, user?.full_name]);

  const guardianDisplayName = useMemo(
    () => resolveGuardianDisplayName(user) || fetchedGuardianName,
    [fetchedGuardianName, user?.guardian_full_name, user?.guardian],
  );

  const collectorDisplayStatus = useMemo(() => {
    if (!user) return 'Active';
    if (user.deleted_at) return 'Deactivated';
    const s = user.status?.trim();
    if (s) return s;
    return 'Active';
  }, [user]);

  const collectorHeaderShowsAvatarImage = useMemo(() => profileHasUploadedPhoto(user ?? undefined), [user]);

  const openUserHeaderMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setUserHeaderMenuAnchor(event.currentTarget);
  }, []);

  const closeUserHeaderMenu = useCallback(() => {
    setUserHeaderMenuAnchor(null);
  }, []);

  const openStatusMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setStatusMenuAnchor(event.currentTarget);
  }, []);

  const closeStatusMenu = useCallback(() => {
    setStatusMenuAnchor(null);
  }, []);

  const openAddDependentModal = useCallback(() => {
    setDependentModalTargetId(0);
    setDependentModalOpen(true);
  }, []);

  const openEditDependentModal = useCallback((dependentId: number) => {
    setDependentModalTargetId(dependentId);
    setDependentModalOpen(true);
  }, []);

  const closeDependentModal = useCallback(() => {
    setDependentModalOpen(false);
    setDependentModalTargetId(0);
  }, []);

  const handleDependentModalSave = useCallback(
    async (savedDependentId: number, isNew: boolean) => {
      if (!userId) return;
      try {
        const u = await getCollectorUserById(Number(userId));
        setUser(u);
      } catch (e) {
        displayErrorDetails(e);
      }
      if (isNew) {
        navigate(`/users/${savedDependentId}`);
      }
    },
    [userId, navigate],
  );

  const toggleDeactivatedDependents = useCallback(() => {
    setDeactivatedSectionOpen((open) => !open);
  }, []);

  const getInvitationDomain = (): string => {
    const org =
      user?.organizations.find((org) => org.id !== 'dentistry-one') ??
      user?.organizations.find((org) => org.id === 'dentistry-one');

    if (!org) return '';

    const divisionDomain = org.divisions?.[0]?.domain;
    const practiceDomain = org.practices?.[0]?.domain;

    const currentDomain = window.location.hostname;
    const fallbackDomain =
      currentDomain === 'localhost' || currentDomain.startsWith('localhost') ? 'develop.dentistry.one' : currentDomain;

    return divisionDomain || practiceDomain || fallbackDomain;
  };

  const inviteUser = async (): Promise<void> => {
    setIsInvitedUser(true);
    // Two operations to keep distinct: the actual reinvite call, and
    // the post-success status refresh. If `refreshCurrentUser` were
    // inside the same try block, a transient network failure on its
    // follow-up GET would land in the catch below, fail the
    // "User has no email" message check, and surface as a generic
    // invite-failed toast even though the invite itself succeeded.
    try {
      await reinviteUserById(
        userId || '',
        { frontend_website: `https://${getInvitationDomain()}` },
        authUser.role.name,
      );
    } catch (error: unknown) {
      // Quick-Add patients (DO-4070) have no email on file, so the
      // backend's reinvite-user endpoint rejects them with
      // `[12254-3] User has no email`. Mirror the Admin pattern from
      // `ModernReviewProfileHeader.tsx`: detect that specific error
      // and open `AddEmailModal`, which collects an email, calls
      // `updateUserEmail`, and re-runs `inviteUser`. Other failures
      // fall through to the standard error toast.
      const responseMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      if (
        responseMessage?.includes('User has no email') ||
        responseMessage?.includes('12254-3') ||
        responseMessage?.includes('12209-3')
      ) {
        setDisplayEmailModal(true);
      } else {
        displayErrorDetails(error);
      }
      setIsInvitedUser(false);
      return;
    }
    // The refresh's `getCollectorUserById` GET can throw (transient
    // 5xx, etc.). Using `try/finally` guarantees `isInvitedUser`
    // clears regardless — otherwise an unhandled throw here would
    // leave the "Invite user" menu item disabled until the next
    // page reload.
    try {
      await refreshCurrentUser();
    } finally {
      setIsInvitedUser(false);
    }
  };

  const refreshCurrentUser = useCallback(async (): Promise<void> => {
    if (!userId) return;
    const updatedUser = await getCollectorUserById(Number(userId));
    setUser(updatedUser);
  }, [userId]);

  const handleConfirmDeactivateCollectorUser = useCallback(
    async (targetUserId: number): Promise<void> => {
      try {
        await deleteCollectorUserById(targetUserId);
        toast.success('User has been deactivated.', { className: 'fs-unmask' });
        await refreshCurrentUser();
      } catch (error) {
        displayErrorDetails(error);
      }
    },
    [refreshCurrentUser],
  );

  const handleConfirmReactivateCollectorUser = useCallback(
    async (targetUserId: number): Promise<void> => {
      try {
        await reactivateCollectorUserById(targetUserId);
        toast.success('User has been activated.', { className: 'fs-unmask' });
        await refreshCurrentUser();
      } catch (error) {
        displayErrorDetails(error);
      }
    },
    [refreshCurrentUser],
  );

  const selectSameAsGuardian = useCallback(
    (isSelect = true): void => {
      if (isSelect) {
        setAddress1(guardianAddress?.address1 ?? '');
        setAddress2(guardianAddress?.address2 ?? '');
        setCity(guardianAddress?.city ?? '');
        setStateValue(guardianAddress?.state ?? '');
        setZipCode(guardianAddress?.zip_code ?? '');
        return;
      }
      setAddress1('');
      setAddress2('');
      setCity('');
      setStateValue('');
      setZipCode('');
    },
    [guardianAddress],
  );

  const handleConfirmDeleteDependent = useCallback(
    async (_targetUserId: number): Promise<void> => {
      if (!user || !isDependentUser) return;
      try {
        if (user.guardian_id) {
          await deleteCollectorDependent(Number(user.guardian_id), user.id);
        } else {
          await deleteCollectorUserById(user.id);
        }
        toast.success('Dependent has been deleted.', { className: 'fs-unmask' });
        navigate('/users');
      } catch (error) {
        displayErrorDetails(error);
      }
    },
    [user, isDependentUser, navigate],
  );

  const currentProfilePayload = useMemo(() => {
    return mapCollectorUserEditFormToApiPayload(
      {
        firstName,
        middleName,
        lastName,
        preferredName,
        dateOfBirth: dobToApi(dateOfBirth),
        gender,
        genderIdentity: genderIdentityValue,
        pronouns,
        selectedLanguages,
        address1,
        address2,
        city,
        state,
        zipCode,
        phoneNumber,
        selectedClientTagIds,
        selectedClientDivisionId,
        guardianRelationship,
        email,
        externalId,
      },
      { isDependent: isDependentUser },
    );
  }, [
    firstName,
    middleName,
    lastName,
    preferredName,
    dateOfBirth,
    gender,
    genderIdentityValue,
    pronouns,
    selectedLanguages,
    address1,
    address2,
    city,
    state,
    zipCode,
    phoneNumber,
    selectedClientTagIds,
    selectedClientDivisionId,
    guardianRelationship,
    email,
    externalId,
    isDependentUser,
  ]);

  const isProfileDirty = useMemo(() => {
    if (savedProfilePayloadRef.current === null) return false;
    return !isEqual(currentProfilePayload, savedProfilePayloadRef.current);
  }, [currentProfilePayload]);

  const isDirty =
    activeTab === 'Profile'
      ? isProfileDirty
      : activeTab === 'Medical history'
      ? medicalHistoryDirty
      : activeTab === 'Insurance'
      ? insuranceDirty
      : false;

  const { showModal, confirmNavigation, saveChanges, hideModal } = useUnsavedChangesWarning(isDirty, formRef);

  const handleSave = useCallback(async () => {
    if (!user) return;
    if (authUser && Number(authUser.id) === Number(user.id)) {
      toast.error('Use My Profile to update your own account.', { className: 'fs-unmask' });
      return;
    }

    const payload = mapCollectorUserEditFormToApiPayload(
      {
        firstName,
        middleName,
        lastName,
        preferredName,
        dateOfBirth: dobToApi(dateOfBirth),
        gender,
        genderIdentity: genderIdentityValue,
        pronouns,
        selectedLanguages,
        address1,
        address2,
        city,
        state,
        zipCode,
        phoneNumber,
        selectedClientTagIds,
        selectedClientDivisionId,
        guardianRelationship,
        email,
        externalId,
      },
      { isDependent: isDependentUser },
    );

    if (isDependentUser && !guardianRelationship.trim()) {
      toast.error('Relationship is required for dependents.', { className: 'fs-unmask' });
      return;
    }

    if (!isDependentUser && divisionRequired && !selectedClientDivisionId) {
      toast.error('Select a division.', { className: 'fs-unmask' });
      return;
    }

    if (!isDependentUser && clientTagOptions.length > 0 && selectedClientTagIds.length === 0) {
      toast.error('Select a client tag.', { className: 'fs-unmask' });
      return;
    }

    const validationError = validateUpdateCollectorUserPayload(payload, {
      isDependent: isDependentUser,
    });
    if (validationError) {
      toast.error(validationError, { className: 'fs-unmask' });
      return;
    }

    setSaving(true);
    try {
      const updatedUser = await updateCollectorUser(user.id, payload);
      const updatedClientTagIds = primaryClientTagIdsFromUserClients(updatedUser.clients);
      const updatedClientDivisionId = updatedUser.divisions?.[0]?.id ?? null;
      setUser(updatedUser);
      setDateOfBirth(dobFromApi(updatedUser.basic_info?.date_of_birth || ''));
      setSelectedClientTagIds(updatedClientTagIds);
      setSelectedClientDivisionId(updatedClientDivisionId);
      if (isDependentUser) {
        const nextEmail = (payload.email || '').trim();
        if ((user.email || '').trim() !== nextEmail) {
          await updateVerifiedEmail(user.id, { email: nextEmail });
        }
      }
      savedProfilePayloadRef.current = {
        ...payload,
        ...(isDependentUser
          ? {}
          : {
              client_ids: updatedClientTagIds,
              client_division_ids: updatedClientDivisionId ? [updatedClientDivisionId] : [],
            }),
      };
      toast.success('Your changes have been saved');
    } catch (error) {
      displayCollectorUserPatchError(error);
    } finally {
      setSaving(false);
    }
  }, [
    user,
    firstName,
    middleName,
    lastName,
    preferredName,
    dateOfBirth,
    gender,
    genderIdentityValue,
    pronouns,
    selectedLanguages,
    phoneNumber,
    address1,
    address2,
    city,
    state,
    zipCode,
    clientTagOptions,
    selectedClientTagIds,
    selectedClientDivisionId,
    divisionRequired,
    guardianRelationship,
    isDependentUser,
    authUser?.id,
    email,
    externalId,
  ]);

  const collectorStatusPillClass =
    collectorDisplayStatus === 'Active'
      ? styles.statusPillReadOnlyActive
      : collectorDisplayStatus === 'Deactivated'
      ? styles.statusPillReadOnlyDeactivated
      : styles.statusPillReadOnlyOther;

  if (loading) {
    return (
      <Container patientDashboard>
        <div className={styles.container}>
          <div className={styles.pageSpinnerWrap}>
            <Loading dotted isHeightAuto />
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container patientDashboard>
      <div className={styles.container}>
        <Button
          type="button"
          className={styles.backButton}
          onClick={() => navigate(-1)}
          disableRipple
          sx={{ textTransform: 'none', minWidth: 0, alignSelf: 'flex-start', width: 'auto' }}
        >
          <IoChevronBack />
          <Typography component="span">Back</Typography>
        </Button>

        <div className={styles.collectorProfileColumn}>
          {user && (
            <>
              <header className={styles.collectorEditUserHeader}>
                <div className={styles.collectorEditUserHeaderIdentity}>
                  <div className={styles.collectorEditUserHeaderAvatar} aria-hidden={!headerDisplayName}>
                    {collectorHeaderShowsAvatarImage && user.id && !headerAvatarLoadFailed ? (
                      <img
                        src={getUserAvatar(user.id)}
                        alt=""
                        className={`fs-exclude ${styles.collectorEditUserHeaderAvatarImg}`}
                        onError={() => setHeaderAvatarLoadFailed(true)}
                      />
                    ) : (
                      <span className={styles.collectorEditUserHeaderAvatarInitials}>
                        {headerDisplayName ? getInitials(headerDisplayName) : '?'}
                      </span>
                    )}
                  </div>
                  <div className={styles.collectorEditUserHeaderText}>
                    <h1 className={styles.collectorEditUserHeaderName}>
                      {headerDisplayName}
                      {isDependentUser && <span className={styles.dependentLabel}> (dependent)</span>}
                    </h1>
                    <div className={styles.userStatusRow}>
                      <span className={styles.userStatusLabel}>User status:</span>
                      <button
                        type="button"
                        className={`${styles.statusPillReadOnly} ${collectorStatusPillClass} ${styles.statusPillAction}`}
                        onClick={openStatusMenu}
                        aria-label="Change user status"
                        aria-controls={statusMenuAnchor ? 'collector-user-status-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={statusMenuAnchor ? 'true' : undefined}
                      >
                        <span>{collectorDisplayStatus}</span>
                        <IoChevronDown className={styles.statusPillChevron} aria-hidden />
                      </button>
                      <Menu
                        id="collector-user-status-menu"
                        anchorEl={statusMenuAnchor}
                        open={Boolean(statusMenuAnchor)}
                        onClose={closeStatusMenu}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      >
                        {user.status === 'Deactivated' ? (
                          <MenuItem
                            sx={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}
                            onClick={() => {
                              closeStatusMenu();
                              setActivateUserModal('reactivate');
                            }}
                          >
                            Activate
                          </MenuItem>
                        ) : (
                          <MenuItem
                            sx={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}
                            onClick={() => {
                              closeStatusMenu();
                              setDeactivateUserModal('deactivated');
                            }}
                          >
                            Deactivate
                          </MenuItem>
                        )}
                      </Menu>
                    </div>
                    {isDependentUser && guardianDisplayName && user.guardian_id && (
                      <div className={styles.userStatusRow}>
                        <span className={styles.userStatusLabel}>Guardian:</span>
                        <button
                          type="button"
                          className={styles.guardianLink}
                          onClick={() => navigate(`/users/${user.guardian_id}`)}
                        >
                          {guardianDisplayName}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.collectorEditUserHeaderActions}>
                  <button
                    type="button"
                    className={styles.collectorEditUserCreateCaseLink}
                    onClick={() =>
                      userId != null &&
                      navigateRaw(`${COLLECTOR_CASE_CREATION_BASE_PATH}/patient/select`, {
                        state: { preselectProfileUserId: Number(userId) },
                      })
                    }
                  >
                    <IoAdd aria-hidden />
                    Create new case
                  </button>
                  {user.role.name !== UserRoleName.Dependent && (
                    <>
                      <IconButton
                        aria-label="More actions"
                        aria-controls={userHeaderMenuAnchor ? 'collector-user-header-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={userHeaderMenuAnchor ? 'true' : undefined}
                        onClick={openUserHeaderMenu}
                      >
                        <IoEllipsisVertical />
                      </IconButton>
                      <Menu
                        id="collector-user-header-menu"
                        anchorEl={userHeaderMenuAnchor}
                        open={Boolean(userHeaderMenuAnchor)}
                        onClose={closeUserHeaderMenu}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      >
                        <MenuItem
                          onClick={() => {
                            closeUserHeaderMenu();
                            inviteUser();
                          }}
                          disabled={
                            user.role.name === 'Dependent' ||
                            ['Active', 'Pending', 'Deactivated'].includes(user?.status) ||
                            isInvitedUser
                          }
                        >
                          <ListItemIcon>
                            <IoAdd />
                          </ListItemIcon>
                          <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                            Invite user
                          </ListItemText>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            closeUserHeaderMenu();
                            setResetPasswordConfirmation({ isOpen: true, userState: 'Active' });
                          }}
                          disabled={['Deactivated', 'Not Invited', 'Invited'].includes(user?.status) || resetEmailSent}
                        >
                          <ListItemIcon>
                            <HttpsOutlinedIcon />
                          </ListItemIcon>
                          <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                            Reset password
                          </ListItemText>
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </div>
              </header>

              <Alert
                severity="info"
                className={addNewUserStyles.patientProfileNotice}
                sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
              >
                You are editing <strong>{headerDisplayName}</strong>&apos;s profile.
              </Alert>
            </>
          )}

          <div className={styles.tabsCollector} role="tablist" aria-label="Profile sections">
            {visibleProfileTabs.map((tab) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={activeTab === tab}
                className={`${styles.tabCollector} ${activeTab === tab ? styles.tabCollectorActive : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          {activeTab === 'Profile' && (
            <Box className={addNewUserStyles.formRoot}>
              <Box
                component="form"
                ref={formRef}
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleSave();
                }}
                onKeyDown={(e) => {
                  // Only a real `<form>` since the unsaved-changes modal needs to submit it;
                  // guard against the native browser behavior of implicitly submitting on Enter
                  // in a text field, which didn't exist when this was a plain `<Box>`.
                  if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
                    e.preventDefault();
                  }
                }}
                className={addNewUserStyles.form}
                style={{ gap: '32px' }}
              >
                <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <TextField
                    name="firstName"
                    label="First name"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    wrapperClassName={addNewUserStyles.inputWrap}
                  />
                  <TextField
                    name="middleName"
                    label="Middle name (optional)"
                    value={middleName}
                    onChange={(e) => setMiddleName(e.target.value)}
                    wrapperClassName={addNewUserStyles.inputWrap}
                  />
                  <TextField
                    name="lastName"
                    label="Last name"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    wrapperClassName={addNewUserStyles.inputWrap}
                  />
                </Box>

                <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <TextField
                    name="preferredName"
                    label="Preferred name (optional)"
                    value={preferredName}
                    onChange={(e) => setPreferredName(e.target.value)}
                    wrapperClassName={addNewUserStyles.inputWrap}
                  />
                  <TextField
                    name="dateOfBirth"
                    label="Date of birth"
                    value={dateOfBirth}
                    onChange={(e) =>
                      dateAutoSlashes(e, 'dateOfBirth', (key: string, val: string) => {
                        if (key === 'dateOfBirth') setDateOfBirth(val);
                      })
                    }
                    maxLength={10}
                    wrapperClassName={addNewUserStyles.inputWrap}
                  />
                </Box>

                <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <TextField
                    label="Email address"
                    type="email"
                    disabled={!isDependentUser}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    name="email"
                    wrapperClassName={addNewUserStyles.inputWrap}
                  />
                  <Box className={addNewUserStyles.inputWrap}>
                    <PhoneInput
                      value={phoneNumber as E164Number}
                      name="phone_number"
                      onChange={(v) => setPhoneNumber(v ?? '')}
                      label="Phone number"
                      wrapperClassName={addNewUserStyles.inputWrap}
                    />
                  </Box>
                </Box>

                <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Box className={addNewUserStyles.inputWrap}>
                    <MUISelectInput
                      name="gender"
                      label="Gender assigned at birth"
                      value={gender}
                      onChange={(v: string) => setGender(String(v))}
                      options={genderOptions}
                    />
                  </Box>
                  <Box className={addNewUserStyles.inputWrap}>
                    <MUISelectInput
                      name="genderIdentityValue"
                      label="Gender identity"
                      value={genderIdentityValue}
                      onChange={(v: string) => setGenderIdentityValue(String(v))}
                      options={genderIdentity}
                    />
                  </Box>
                  <Box className={addNewUserStyles.inputWrap}>
                    <MUISelectInput
                      name="pronouns"
                      label="Preferred pronouns (optional)"
                      value={pronouns}
                      onChange={(v: string) => setPronouns(String(v))}
                      options={pronounsOptions}
                    />
                  </Box>
                </Box>

                <Box className={addNewUserStyles.row} sx={{ mb: 2 }}>
                  <MultiSelectInputMUI
                    name="languages"
                    label="Languages"
                    required
                    withId
                    styledTag
                    value={selectedLanguages.map((l) => String(l.id))}
                    onChange={(ids: string[]) =>
                      setSelectedLanguages(allLanguages.filter((l) => ids.includes(String(l.id))))
                    }
                    options={allLanguages.map((l) => ({ id: l.id, value: l.id, label: l.name }))}
                    wrapperClassName={addNewUserStyles.inputWrap}
                  />
                </Box>

                {isDependentUser && (
                  <>
                    <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                      <Box className={addNewUserStyles.inputWrap}>
                        <MUISelectInput
                          name="guardianRelationship"
                          label="Relation"
                          required
                          value={guardianRelationship}
                          onChange={(v: string) => setGuardianRelationship(String(v))}
                          options={relationshipOptions}
                        />
                      </Box>
                    </Box>
                    <Box className={styles.sameAddressCheckboxRow}>
                      <Checkbox
                        checked={isSameAddressAsGuardian}
                        onChange={(_event, checked) => selectSameAsGuardian(checked)}
                        disabled={!guardianAddress?.address1}
                      />
                      <span className={styles.sameAddressCheckboxLabel}>Same address as guardian</span>
                    </Box>
                  </>
                )}

                <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <TextField
                    name="address1"
                    label="Address"
                    required
                    value={address1}
                    disabled={isSameAddressAsGuardian}
                    onChange={(e) => setAddress1(e.target.value)}
                    wrapperClassName={addNewUserStyles.inputWrap}
                  />
                  <TextField
                    name="address2"
                    label="Apartment, suite, etc. (optional)"
                    value={address2}
                    disabled={isSameAddressAsGuardian}
                    onChange={(e) => setAddress2(e.target.value)}
                    wrapperClassName={addNewUserStyles.inputWrap}
                  />
                </Box>

                <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                  <TextField
                    name="city"
                    label="City"
                    required
                    value={city}
                    disabled={isSameAddressAsGuardian}
                    onChange={(e) => setCity(e.target.value)}
                    wrapperClassName={addNewUserStyles.inputWrap}
                  />
                  <Box className={addNewUserStyles.inputWrap}>
                    <MUISelectInput
                      name="state"
                      label="State"
                      required
                      value={state}
                      disabled={isSameAddressAsGuardian}
                      onChange={(v: string) => setStateValue(String(v))}
                      options={stateOptions}
                    />
                  </Box>
                  <TextField
                    name="zipCode"
                    label="Zip code"
                    required
                    value={zipCode}
                    disabled={isSameAddressAsGuardian}
                    onChange={(e) => setZipCode(e.target.value)}
                    wrapperClassName={addNewUserStyles.inputWrap}
                  />
                </Box>
                {!isDependentUser && clientTagOptions.length > 0 && (
                  <div>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '20px', mb: 2 }}>
                      Client tags
                    </Typography>
                    <p className={styles.clientTagsDescription}>Select your collector client tag for this patient.</p>
                    <div className={addNewUserStyles.inputWrap}>
                      <MUISelectInput
                        name="client_tag_id"
                        label="Client tag"
                        required
                        value={selectedClientTagIds[0] != null ? String(selectedClientTagIds[0]) : ''}
                        options={clientTagOptions}
                        onChange={(value: string) => {
                          const tagId = value ? Number(value) : null;
                          setSelectedClientTagIds(tagId != null ? [tagId] : []);
                          setSelectedClientDivisionId(null);
                        }}
                      />
                    </div>
                  </div>
                )}
                {!isDependentUser && selectedClientTagIds.length === 1 && divisionOptions.length > 0 && (
                  <div>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '20px', mb: 2 }}>
                      Division
                      {divisionRequired && (
                        <Typography component="span" color="error">
                          {' '}
                          *
                        </Typography>
                      )}
                    </Typography>
                    <div className={addNewUserStyles.inputWrap}>
                      <MUISelectInput
                        name="client_division_id"
                        label="Division"
                        required={divisionRequired}
                        value={selectedClientDivisionId != null ? String(selectedClientDivisionId) : ''}
                        options={divisionOptions}
                        onChange={(value: string) =>
                          setSelectedClientDivisionId(value ? Number(value) : null)
                        }
                      />
                    </div>
                  </div>
                )}
                {/* User IDs: staff-only, never shown on a patient's own My Profile.
                    This page is Collector-only, so no separate role check is needed here. */}
                <div>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '20px', mb: 2 }}>
                    User IDs
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                    <TextField
                      name="externalUserId"
                      label="External User ID"
                      value={externalId}
                      onChange={(e) => setExternalId(e.target.value)}
                      maxLength={40}
                      wrapperClassName={addNewUserStyles.inputWrap}
                    />
                    <TextField
                      name="internalUserId"
                      label="Internal User ID"
                      value={String(user.id)}
                      disabled
                      wrapperClassName={addNewUserStyles.inputWrap}
                    />
                  </Box>
                </div>
                {isPatientUser && (
                  <>
                    <div className={styles.dependentsHeader}>
                      <h2 className={styles.sectionTitle} style={{ margin: 0 }}>
                        Dependents
                      </h2>
                      <button type="button" className={styles.addDependentBtn} onClick={openAddDependentModal}>
                        <IoAdd aria-hidden />
                        Add dependent
                      </button>
                    </div>
                    {activeDependents.length > 0 && (
                      <div className={styles.dependentList}>
                        {activeDependents.map((dep) => {
                          const showDepAvatarImg = profileHasUploadedPhoto(dep) && !dependentAvatarFailedIds[dep.id];
                          return (
                            <div key={dep.id} className={styles.dependentRow}>
                              <div className={styles.dependentAvatar}>
                                {showDepAvatarImg ? (
                                  <img
                                    src={getUserAvatar(dep.id)}
                                    alt=""
                                    className="fs-exclude"
                                    onError={() => setDependentAvatarFailedIds((prev) => ({ ...prev, [dep.id]: true }))}
                                  />
                                ) : dep.full_name ? (
                                  getInitials(dep.full_name)
                                ) : (
                                  '?'
                                )}
                              </div>
                              <div className={styles.dependentInfo}>
                                <span className={styles.dependentName}>{dep.full_name}</span>
                                <button
                                  type="button"
                                  className={styles.editProfileLink}
                                  onClick={() => openEditDependentModal(dep.id)}
                                >
                                  Edit profile
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <button type="button" className={styles.viewDeactivatedLink} onClick={toggleDeactivatedDependents}>
                      {deactivatedSectionOpen ? 'Hide deactivated dependents' : 'View deactivated dependents'}
                    </button>
                    {deactivatedSectionOpen && (
                      <div className={styles.deactivatedDependentsSection}>
                        {deletedDependents.length === 0 ? (
                          <Typography component="p" sx={{ fontSize: 14, color: 'text.secondary' }}>
                            No deleted dependents
                          </Typography>
                        ) : (
                          <div className={styles.dependentList}>
                            {deletedDependents.map((dep) => (
                              <div key={dep.id} className={`${styles.dependentRow} ${styles.dependentRowDeactivated}`}>
                                <div className={styles.dependentAvatar}>
                                  {dep.full_name ? getInitials(dep.full_name) : '?'}
                                </div>
                                <div className={styles.dependentInfo}>
                                  <span className={styles.dependentName}>{dep.full_name || '—'}</span>
                                  <span className={styles.dependentStatusMuted}>Deleted</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                <Box className={addNewUserStyles.actions} sx={{ mt: 2 }}>
                  <Button
                    type="button"
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || !isProfileDirty}
                    className={addNewUserStyles.saveChangesButton}
                  >
                    {saving ? 'Saving…' : 'Save changes'}
                  </Button>
                  {isDependentUser && (
                    <button
                      type="button"
                      className={styles.dependentDeleteBtn}
                      onClick={() => setDeleteDependentModal('delete')}
                    >
                      Delete dependent
                    </button>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {activeTab === 'Medical history' && (
            <MedicalHistoryTab user={user} formRef={formRef} setIsDirty={setMedicalHistoryDirty} />
          )}
          {activeTab === 'Insurance' && (
            <InsuranceTab
              user={user}
              formRef={formRef}
              setIsDirty={setInsuranceDirty}
              onUserSaved={async () => {
                if (!userId) return;
                try {
                  const u = await getCollectorUserById(Number(userId));
                  setUser(u);
                } catch (e) {
                  displayErrorDetails(e);
                }
              }}
            />
          )}
          {activeTab === 'Pharmacy' && (
            <PharmacyTab
              user={user}
              onUserSaved={async () => {
                if (!userId) return;
                try {
                  const u = await getCollectorUserById(Number(userId));
                  setUser(u);
                } catch (e) {
                  displayErrorDetails(e);
                }
              }}
            />
          )}
          {activeTab === 'Cases' && config.features.multiStage && user && (
            <ProfileCasesSection patientId={Number(userId)} />
          )}
          {activeTab === 'Account settings' && !isDependentUser && (
            <AccountSettingsTab
              user={user}
              onUserSaved={async () => {
                if (!userId) return;
                try {
                  const u = await getCollectorUserById(Number(userId));
                  setUser(u);
                } catch (e) {
                  displayErrorDetails(e);
                }
              }}
            />
          )}
        </div>

        {user && isPatientUser && (
          <CollectorAddDependentModal
            open={dependentModalOpen}
            onClose={closeDependentModal}
            guardianUser={user}
            dependentId={dependentModalTargetId}
            onDependentSaved={handleDependentModalSave}
          />
        )}

        {resetPasswordConfirmation.isOpen && (
          <ResetPasswordModal
            setOpenModal={setResetPasswordConfirmation}
            disableButton={disableButton}
            userId={Number(userId)}
            userEmail={user?.email || ''}
            userName={user?.full_name || ''}
            isDeactivated={user?.status === 'Deactivated'}
            userState={resetPasswordConfirmation.userState}
          />
        )}

        {deactivateUserModal && user && (
          <ChangeUserStatusModal
            setOpenModal={setDeactivateUserModal}
            disableButton={() => undefined}
            userId={user.id}
            userName={user.full_name || user.email || 'User'}
            status="deactivated"
            onConfirmDeactivate={handleConfirmDeactivateCollectorUser}
          />
        )}

        {activateUserModal && user && (
          <ChangeUserStatusModal
            setOpenModal={setActivateUserModal}
            disableButton={() => undefined}
            userId={user.id}
            userName={user.full_name || user.email || 'User'}
            status="reactivate"
            onConfirmReactivate={handleConfirmReactivateCollectorUser}
          />
        )}

        {deleteDependentModal && user && (
          <ChangeUserStatusModal
            setOpenModal={setDeleteDependentModal}
            disableButton={() => undefined}
            userId={user.id}
            userName={user.full_name || user.email || 'User'}
            status="deactivated"
            onConfirmDeactivate={handleConfirmDeleteDependent}
          />
        )}

        {displayEmailModal && user && (
          <AddEmailModal
            isOpen={displayEmailModal}
            onClose={() => setDisplayEmailModal(false)}
            userId={String(user.id)}
            thisUser={user}
            setThisUser={setUser as React.Dispatch<React.SetStateAction<UserProfile>>}
            inviteUser={inviteUser}
          />
        )}

        {showModal && (
          <UnsavedChangesModal
            isOpen={showModal}
            onConfirm={confirmNavigation}
            saveChange={saveChanges}
            loading={false}
            hideModal={hideModal}
          />
        )}
      </div>
    </Container>
  );
};

export default EditUserPage;
